/**
 * Who changed what, and when.
 *
 * The security sentence at the top stays whatever else changes. This screen is
 * what a customer's security reviewer will screenshot, and the two guarantees it
 * states are the ones they came to check.
 */

import React, { useEffect, useRef } from 'react';
import { Panel, Button, Select } from '@cloudsam/ui';

function when(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
}

// The stored action is a code. A person reading an audit page needs a sentence.
// An unmapped code prints itself rather than becoming "Unknown", so a new kind
// of change is visible the day it starts being recorded.
function describe(entry) {
    switch (entry.action) {
        case 'channel.binding.created': return 'Created a binding';
        case 'channel.binding.state_changed': return `Moved the binding to ${entry.details?.new_state ?? 'a new state'}`;
        case 'channel.binding.credential_replaced': return 'Replaced the credential';
        case 'channel.binding.route_key_set': return 'Set the receiving identity';
        case 'channel.binding.made_default': return 'Made this the default binding';
        case 'channel.binding.default_cleared': return 'Cleared the default binding';
        case 'channel.defaults.saved': return 'Changed the binding defaults';
        case 'channel.test_recipient.added': return 'Added a test recipient';
        case 'channel.test_recipient.removed': return 'Removed a test recipient';
        default: return entry.action;
    }
}

export default function AuditView({
    entries = [], total = 0, page = 1, rows = 25,
    onPage, onRows, search = '', onSearch, error, loading = false, unavailableNote,
}) {
    const pages = Math.max(1, Math.ceil(total / rows));
    const listBodyRef = useRef(null);
    const shouldFocusList = useRef(false);

    useEffect(() => {
        if (!shouldFocusList.current) return;
        listBodyRef.current?.focus();
        shouldFocusList.current = false;
    }, [page]);

    function changePage(nextPage) {
        shouldFocusList.current = true;
        onPage?.(nextPage);
    }

    const actions = (
        <>
            <input
                className="input"
                placeholder="Search audit entries"
                value={search}
                onChange={onSearch}
                aria-label="Search audit entries"
            />
            <Button variant="ghost">Export ▾</Button>
        </>
    );

    return (
        <Panel title="Audit trail" count={`${total} entries`} actions={actions}
            className="managed-history-panel" bodyClassName="managed-history-panel__body">
            <div className="security-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="10" rx="2" />
                    <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
                </svg>
                <span>
                    Secrets are masked after entry and replace-only — there is no reveal, for anyone.
                    Every change below is recorded automatically and cannot be edited or deleted.
                </span>
            </div>

            <div className="managed-history-scroll" ref={listBodyRef} tabIndex="-1" aria-label="Audit trail list">
                <div className="table-head grid-audit managed-history-head">
                    <div>When</div><div>Who</div><div>Change</div><div>Binding</div>
                </div>
                {error ? (
                    <div className="managed-list-state panel-error">{error}</div>
                ) : unavailableNote ? (
                    <div className="managed-list-state panel-unavailable">{unavailableNote}</div>
                ) : loading ? (
                    <div className="managed-list-state">Loading audit entries…</div>
                ) : entries.length === 0 ? (
                    <div className="managed-list-state panel-note">
                        {search.trim() ? `No audit entries match “${search.trim()}”.` : 'Nothing has been changed yet.'}
                    </div>
                ) : (
                    entries.map((e) => (
                        <div className="table-row grid-audit" key={e.id}>
                            <div className="cell-muted">{when(e.at)}</div>
                            <div>{e.actor_name || e.actor_email || 'System'}</div>
                            <div>{describe(e)}</div>
                            <div className="cell-muted">{e.binding_label || '—'}</div>
                        </div>
                    ))
                )}
            </div>
            <div className="pager" aria-label="Audit trail pagination">
                <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => changePage(page - 1)}>Previous</Button>
                <span>Page {page} of {pages} · {total} total</span>
                <Button variant="secondary" disabled={page >= pages || loading} onClick={() => changePage(page + 1)}>Next</Button>
                <span>Rows</span>
                <Select value={rows} onChange={onRows} disabled={loading} aria-label="Audit trail rows per page">
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </Select>
            </div>
        </Panel>
    );
}
